import { BlogPost, getPostData, getSortedPostsData } from '@/../lib/posts';
import { Container } from '@/components/Container';
import Footer from '@/components/Footer';
import { Header } from '@/components/Header';
import StructuredData from '@/components/StructuredData';
import Head from 'next/head';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TimeAgo from 'timeago-react';
import { useEffect } from 'react';
import { track_event } from '@/lib/mixpanel';
import Image from 'next/image';
import { NextSeo } from 'next-seo';

const Article = (props: BlogPost) => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'BlogPosting',
    headline: props.title,
    datePublished: new Date(props.date).toISOString(),
    dateModified: new Date(props.modified).toISOString(),
    author: {
      '@type': 'Person',
      '@id': `https://snap2pass.com/post/${props.pid}/#author`,
      name: props.author,
      url: `https://snap2pass.com/post/${props.pid}/#author`,
    },
    publisher: {
      '@type': 'Organization',
      '@id': 'https://snap2pass.com/#organization',
      name: 'Snap2Pass',
    },
    url: `https://snap2pass.com/post/${props.pid}`,
    description: props.description,
    name: props.title,
    '@id': `https://snap2pass.com/post/${props.pid}/#snipped`,
    isPartOf: {
      '@type': 'Blog',
      '@id': 'https://www.snap2pass.com/blog',
      name: 'Snap2Pass Blog',
    },
    image: {
      '@id': `https://www.snap2pass.com${props.cover}`,
    },
    inLanguage: 'en-US',
    mainEntityOfPage: {
      '@id': `https://snap2pass.com/post/${props.pid}/#page`,
    },
  };
};

export default function Post(props: BlogPost) {
  useEffect(() => {
    const url = new URL(window.location.href);
    track_event('page_view', {
      path: props.pid,
      post_title: props.title,
      'via_google_ads:': url?.searchParams.get('gclid') != undefined,
    });
  });

  return (
    <>
      <NextSeo
        title={props.title}
        description={props.description}
        canonical={`https://www.snap2pass.com/post/${props.pid}`}
        openGraph={{
          url: `https://www.snap2pass.com/post/${props.pid}`,
          title: props.title,
          description: props.description,
          images: [
            {
              url: `https://www.snap2pass.com${props.cover}`,
              width: 1200,
              height: 630,
              alt: props.title,
            },
          ],
          site_name: 'Snap2Pass',
        }}
        twitter={{
          handle: '@snap2pass',
          site: '@snap2pass',
          cardType: 'summary_large_image',
        }}
      />
      <Head>
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Header />
      <main>
        <section
          id="blog_post"
          aria-label={props.title}
          className="relative overflow-hidden pb-28 py:5 lg:py-10"
        >
          <StructuredData key={''} data={Article(props)} />
          <Container className="relative">
            <div className="mx-auto prose mt-5">
              <div className="group block flex-shrink-0 not-prose mb-5">
                <div className="flex items-center">
                  <div>
                    <Image
                      className="inline-block h-[50px] w-[50px] ring-4 rounded-full"
                      src={props.author.image}
                      alt="Passport photo expert"
                      width={50}
                      height={50}
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {props.author.name}
                    </p>
                    <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                      {props.author.title}
                    </p>
                    <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                      <time dateTime={props.date}>
                        <TimeAgo datetime={props.date} />
                      </time>
                      {` · ${props.readTime} min read`}
                    </p>
                  </div>
                </div>
              </div>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
              >
                {props.markdown}
              </ReactMarkdown>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </>
  );
}

type Params = {
  params: {
    pid: string;
  };
};

export async function getStaticProps({ params }: Params) {
  const postData = getPostData(params.pid);
  // Pass post data to the page via props
  return { props: { ...postData } };
}

// This function gets called at build time
export async function getStaticPaths() {
  // Get the paths we want to pre-render
  const paths = getSortedPostsData().map((postData) => ({
    params: { pid: postData.pid },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
}
